import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, ChevronRight } from "lucide-react";
import { CaseStudyItem, CaseStudySection, CaseStudySectionType, ContentBlockType, ContentBlock } from "@/types/case-study";
import { CaseStudySectionEditor } from "./CaseStudySectionEditor";
import { SectionSelector } from "./SectionSelector";
import { CaseStudyDetailsCard } from "./CaseStudyDetailsCard";
import { useCaseStudy } from "@/hooks/useCaseStudy";
import { CreateCaseStudyData } from "@/services/supabase/caseStudies";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCaseStudyFormState } from "@/hooks/domains/case-study/useCaseStudyFormState";
import { useNavigate } from "react-router-dom";
import { useCoverImageUpload } from "@/hooks/domains/case-study/useCoverImageUpload";
import { useSectionOperations } from "@/hooks/domains/case-study/useSectionOperations";
import { useContentBlockOperations } from "@/hooks/domains/case-study/useContentBlockOperations";
import { useSectionScrolling } from "@/hooks/domains/case-study/useSectionScrolling";
import { useSectionDragDrop } from "@/hooks/domains/case-study/useSectionDragDrop";

interface CaseStudyFormProps {
  onSave: (caseStudy: CaseStudyItem) => void;
  onCancel: () => void;
  onPreview: (caseStudy: CaseStudyItem) => void;
  initialData?: CaseStudyItem;
  // Form state props (optional for backward compatibility)
  caseStudy?: CaseStudyItem;
  setCaseStudy?: (caseStudy: CaseStudyItem | ((prev: CaseStudyItem) => CaseStudyItem)) => void;
  activeSection?: string | null;
  setActiveSection?: (sectionId: string | null) => void;
  showSectionSelector?: boolean;
  setShowSectionSelector?: (show: boolean) => void;
  onAddSectionFromHeader?: (handler: (sectionType: string) => void) => void;
  onAddContentBlockFromHeader?: (handler: (blockType: string) => void) => void;
  isDetailsCollapsed?: boolean;
  setIsDetailsCollapsed?: (collapsed: boolean) => void;
  onToggleCollapseAll?: () => void;
  areAllCollapsed?: boolean;
  titleError?: string;
  setTitleError?: (error: string) => void;
  isTitleValid?: boolean;
  validateTitle?: (title: string) => boolean;
  // Save trigger props
  saveTrigger?: { asDraft: boolean } | null;
  onSaveTriggered?: () => void;
  // Content change callback for validation clearing
  onSectionContentChanged?: (sectionId: string, oldContent: string, newContent: string) => void;
  // Section operation callbacks
  onAddSection?: (type: CaseStudySectionType) => void;
  onAddCustomSection?: (title: string, description?: string) => void;
  // Scrolling callback
  onScrollToSection?: (sectionId: string) => void;
}

// Sortable wrapper for case study sections
const SortableCaseStudySection = ({
  section,
  isActive,
  onUpdate,
  onRemove,
  onActivate,
  canDelete,
  onUpdateContentBlock,
  onRemoveContentBlock,
  onAddContentBlock,
  onAddSectionFromElement,
  sectionRef,
  isCollapsed,
  onToggleCollapse,
}: {
  section: CaseStudySection;
  isActive: boolean;
  onUpdate: (updates: Partial<CaseStudySection>) => void;
  onRemove: () => void;
  onActivate: () => void;
  canDelete: boolean;
  onUpdateContentBlock?: (blockId: string, content: Record<string, unknown>) => void;
  onRemoveContentBlock?: (blockId: string) => void;
  onAddContentBlock?: (type: ContentBlockType) => void;
  onAddSectionFromElement?: (elementType: ContentBlockType, insertAfterSectionId: string) => void;
  sectionRef?: (element: HTMLDivElement | null) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.id,
    animateLayoutChanges: () => true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div
      ref={(element) => {
        setNodeRef(element);
        if (sectionRef) {
          sectionRef(element);
        }
      }}
      style={style}
      {...attributes}
      className={`${isDragging ? "isDragging" : ""}`}
    >
      <CaseStudySectionEditor
        section={section}
        isActive={isActive}
        onUpdate={onUpdate}
        onRemove={onRemove}
        onActivate={onActivate}
        dragHandleProps={listeners}
        canDelete={canDelete}
        onUpdateContentBlock={onUpdateContentBlock}
        onRemoveContentBlock={onRemoveContentBlock}
        onAddContentBlock={onAddContentBlock}
        onAddSectionFromElement={onAddSectionFromElement}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />
    </div>
  );
};

export const CaseStudyForm: React.FC<CaseStudyFormProps> = ({
  onSave,
  onCancel,
  onPreview,
  initialData,
  // Form state props
  caseStudy: propCaseStudy,
  setCaseStudy: propSetCaseStudy,
  activeSection: propActiveSection,
  setActiveSection: propSetActiveSection,
  showSectionSelector: propShowSectionSelector,
  setShowSectionSelector: propSetShowSectionSelector,
  onAddSectionFromHeader,
  onAddContentBlockFromHeader,
  isDetailsCollapsed: propIsDetailsCollapsed,
  setIsDetailsCollapsed: propSetIsDetailsCollapsed,
  onToggleCollapseAll,
  areAllCollapsed,
  titleError: propTitleError,
  setTitleError: propSetTitleError,
  isTitleValid: propIsTitleValid,
  validateTitle: propValidateTitle,
  // Save trigger props
  saveTrigger,
  onSaveTriggered,
  // Content change callback
  onSectionContentChanged,
  // Section operation callbacks
  onAddSection: propOnAddSection,
  onAddCustomSection: propOnAddCustomSection,
  // Scrolling callback
  onScrollToSection: propOnScrollToSection,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [savingAsDraft, setSavingAsDraft] = useState(false);

  // Use Supabase hooks
  const {
    createCaseStudy,
    updateCaseStudy,
    addSection,
    updateSection,
  } = useCaseStudy();
  
  const queryClient = useQueryClient();

  // Form state management - use props if provided, otherwise use internal state
  const internalState = useCaseStudyFormState({ initialData });
  
  const caseStudy = propCaseStudy || internalState.caseStudy;
  const setCaseStudy = propSetCaseStudy || internalState.setCaseStudy;
  const activeSection = propActiveSection !== undefined ? propActiveSection : internalState.activeSection;
  const setActiveSection = propSetActiveSection || internalState.setActiveSection;
  const showSectionSelector = propShowSectionSelector !== undefined ? propShowSectionSelector : internalState.showSectionSelector;
  const setShowSectionSelector = propSetShowSectionSelector || internalState.setShowSectionSelector;
  const isDetailsCollapsed = propIsDetailsCollapsed !== undefined ? propIsDetailsCollapsed : internalState.isDetailsCollapsed;
  const setIsDetailsCollapsed = propSetIsDetailsCollapsed || internalState.setIsDetailsCollapsed;
  const titleError = propTitleError !== undefined ? propTitleError : internalState.titleError;
  const setTitleError = propSetTitleError || internalState.setTitleError;
  const isTitleValid = propIsTitleValid !== undefined ? propIsTitleValid : internalState.isTitleValid;
  const validateTitle = propValidateTitle || internalState.validateTitle;

  const isNewCaseStudy = !initialData?.id;

  const [isDetailsCardActive, setIsDetailsCardActive] = useState(false);
  const updateActiveSection = useCallback((sectionId: string | null) => {
    setIsDetailsCardActive(false);
    setActiveSection(sectionId);
  }, [setActiveSection, setIsDetailsCardActive]);

  // Cover image upload
  const {
    coverImagePreview,
    isDraggingCover,
    isUploadingCover,
    coverImageError,
    coverImageInputRef,
    handleCoverImageClick,
    handleCoverImageDragOver,
    handleCoverImageDragLeave,
    handleCoverImageDrop,
    handleRemoveCoverImage,
    handleCoverImageUpload,
    showCropModal,
    cropModalImageSrc,
    handleCropComplete,
    handleCropCancel,
  } = useCoverImageUpload({
    coverImage: caseStudy.cover_image,
    onCoverImageChange: (url) => setCaseStudy(prev => ({ ...prev, cover_image: url })),
  });

  // Section scrolling
  const { sectionRefs, scrollContainerRef, scrollToSection } = useSectionScrolling();

  // Wrapper function to call both internal and prop scrollToSection
  const wrappedScrollToSection = (sectionId: string) => {
    scrollToSection(sectionId);
    propOnScrollToSection?.(sectionId);
  };

  // Section operations
  const {
    handleAddSection,
    handleAddCustomSection,
    handleAddSectionFromElement,
    handleUpdateSection,
    handleRemoveSection,
  } = useSectionOperations({
    caseStudy,
    setCaseStudy,
    setActiveSection: updateActiveSection,
    scrollToSection: wrappedScrollToSection,
    isNewCaseStudy,
    onSectionContentChanged,
  });

  // Pass handleAddSection to parent via callback
  useEffect(() => {
    if (onAddSectionFromHeader) {
      onAddSectionFromHeader(handleAddSection);
    }
  }, [handleAddSection, onAddSectionFromHeader]);

  // Wrapper functions to call both internal handlers and prop callbacks
  const wrappedHandleAddSection = (type: CaseStudySectionType) => {
    handleAddSection(type);
    propOnAddSection?.(type);
  };

  const wrappedHandleAddCustomSection = (title: string, description?: string) => {
    handleAddCustomSection(title, description);
    propOnAddCustomSection?.(title, description);
  };

  // Content block operations
  const {
    handleAddContentBlock,
    handleUpdateContentBlock,
    handleRemoveContentBlock,
    getDefaultContentForType,
  } = useContentBlockOperations({
    caseStudy,
    setCaseStudy,
    activeSection,
    isNewCaseStudy,
  });

  const activateDetailsCard = useCallback(() => {
    setIsDetailsCardActive(true);
    setActiveSection(null);
  }, [setActiveSection, setIsDetailsCardActive]);

  const activateSectionCard = useCallback((sectionId: string) => {
    updateActiveSection(sectionId);
  }, [updateActiveSection]);

  const clearActiveSelection = useCallback(() => {
    updateActiveSection(null);
  }, [updateActiveSection]);

  // Wrapper for adding content blocks from header - always creates new section at end
  const handleAddContentBlockFromHeaderWrapper = useCallback(async (type: ContentBlockType) => {
    // Get the last section ID if sections exist, otherwise null
    const lastSectionId = caseStudy.sections.length > 0
      ? caseStudy.sections[caseStudy.sections.length - 1].id
      : undefined;

    // Always create a new section at the end with the element
    await handleAddSectionFromElement(type, lastSectionId);
  }, [caseStudy.sections, handleAddSectionFromElement]);

  // Pass handleAddContentBlock wrapper to parent via callback
  useEffect(() => {
    if (onAddContentBlockFromHeader) {
      onAddContentBlockFromHeader(handleAddContentBlockFromHeaderWrapper);
    }
  }, [handleAddContentBlockFromHeaderWrapper, onAddContentBlockFromHeader]);

  // Drag and drop
  const { sensors, handleDragEnd } = useSectionDragDrop({
    caseStudy,
    setCaseStudy,
    isNewCaseStudy,
  });




  const handleSave = useCallback(async (asDraft: boolean = true) => {
    // Prevent concurrent saves - critical for avoiding duplicate case studies
    if (isSaving) {
      return;
    }

    const previousCaseStudy = caseStudy;
    try {
      setIsSaving(true);
      setSavingAsDraft(asDraft);

      // Validate title before saving
      if (!validateTitle(caseStudy.title)) {
        toast({
          title: 'Validation Error',
          description: 'Case study title is required and cannot be empty',
          variant: 'destructive',
        });
        return;
      }
      
      const newStatus = asDraft ? "draft" : "published";
      
      // Update local state immediately for instant UI feedback
      setCaseStudy(prev => ({
        ...prev,
        status: newStatus,
      }));

      // Show immediate feedback
      toast({
        title: asDraft ? 'Saving as draft...' : 'Publishing...',
        description: 'Your case study is being saved',
      });

      if (isNewCaseStudy) {
        // Create new case study with all sections included
        const newCaseStudy = await createCaseStudy.mutateAsync({
          title: caseStudy.title,
          description: caseStudy.description,
          cover_image: caseStudy.cover_image,
          status: newStatus,
          slug: caseStudy.slug,
          seo_title: caseStudy.seo_title,
          seo_description: caseStudy.seo_description,
          og_image: caseStudy.og_image,
          sections: caseStudy.sections, // Include sections in the creation
          creation_method: caseStudy.creation_method || "manual",
        });
        
        // Update local state with the new case study (including the ID)
        setCaseStudy(newCaseStudy);
        onSave(newCaseStudy);
        
        // Show success feedback
        toast({
          title: asDraft ? 'Case study saved!' : 'Case study published!',
          description: asDraft ? 'Your draft has been saved successfully' : 'Your case study has been published',
        });
      } else {
        // Update existing case study
        const updatedCaseStudy = await updateCaseStudy.mutateAsync({
          id: caseStudy.id,
          title: caseStudy.title,
          description: caseStudy.description,
          cover_image: caseStudy.cover_image,
          status: newStatus,
          slug: caseStudy.slug,
          seo_title: caseStudy.seo_title,
          seo_description: caseStudy.seo_description,
          og_image: caseStudy.og_image,
          creation_method: caseStudy.creation_method,
        });
        
        // Update local state with the updated case study
        setCaseStudy(updatedCaseStudy);
        onSave(updatedCaseStudy);
        
        // Show success feedback
        toast({
          title: asDraft ? 'Case study updated!' : 'Case study published!',
          description: asDraft ? 'Your draft has been updated successfully' : 'Your case study has been published',
        });
        
        // Note: Query invalidation is already handled by the mutation's onSuccess callback
      }
    } catch (error) {
      console.error('Error saving case study:', error);
      
      // Show error feedback
      toast({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Failed to save case study. Please try again.',
        variant: 'destructive',
      });
      
      // Revert status on error
      setCaseStudy(previousCaseStudy);
    } finally {
      setIsSaving(false);
      setSavingAsDraft(false);
    }
  }, [caseStudy, setCaseStudy, validateTitle, createCaseStudy, updateCaseStudy, queryClient, isNewCaseStudy, onSave, toast]);

  // Handle save trigger from header
  useEffect(() => {
    if (saveTrigger) {
      const performSave = async () => {
        try {
          // Clear the trigger BEFORE starting the save to prevent re-triggering
          onSaveTriggered?.();
          await handleSave(saveTrigger.asDraft);
        } catch (error) {
          console.error('[CaseStudyForm] Error during save trigger:', error);
        }
      };
      performSave();
    }
  }, [saveTrigger, onSaveTriggered, handleSave]);

  const handlePreview = () => {
    if (caseStudy.id) {
      navigate(`/case-study/preview/${caseStudy.id}`);
    }
  };


  return (
    <div className="w-full pb-16" style={{ scrollBehavior: 'smooth' }}>
    {/* Main Content Area - Full Width */}
    <div
      ref={scrollContainerRef}
      data-scroll-container
      className="w-full overflow-y-auto"
    >
      <div 
        className="bg-white rounded-2xl shadow p-6 md:p-8 w-full"
        onClick={(e) => {
          // Unselect active section when clicking on the main container
          // This will help with scrolling when a section is selected at the bottom
          if ((activeSection || isDetailsCardActive) && e.target === e.currentTarget) {
            clearActiveSelection();
          }
        }}
      >

      {/* Ca se Study Details */}
      <CaseStudyDetailsCard
        caseStudy={caseStudy}
        onUpdate={(updates) => setCaseStudy(prev => ({ ...prev, ...updates }))}
        isCollapsed={isDetailsCollapsed}
        onToggleCollapse={() => setIsDetailsCollapsed(!isDetailsCollapsed)}
        onToggleCollapseAll={onToggleCollapseAll}
        areAllCollapsed={areAllCollapsed}
        isActive={isDetailsCardActive}
        onActivate={activateDetailsCard}
        titleError={titleError}
        onTitleChange={(newTitle) => {
                setCaseStudy(prev => ({ ...prev, title: newTitle }));
                // Clear error when user starts typing
                if (titleError) {
                  setTitleError('');
                }
              }}
        onTitleBlur={validateTitle}
        coverImagePreview={coverImagePreview}
        isDraggingCover={isDraggingCover}
        isUploadingCover={isUploadingCover}
        coverImageError={coverImageError}
        coverImageInputRef={coverImageInputRef}
        onCoverImageClick={handleCoverImageClick}
        onCoverImageDragOver={handleCoverImageDragOver}
        onCoverImageDragLeave={handleCoverImageDragLeave}
        onCoverImageDrop={handleCoverImageDrop}
        onRemoveCoverImage={handleRemoveCoverImage}
        onCoverImageUpload={handleCoverImageUpload}
        showCropModal={showCropModal}
        cropModalImageSrc={cropModalImageSrc}
        onCropComplete={handleCropComplete}
        onCropCancel={handleCropCancel}
      />

      {/* Sections */}
      {caseStudy.sections.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={caseStudy.sections.sort((a, b) => a.order - b.order).map(section => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              className="space-y-4 mb-6"
              onClick={(e) => {
                // Only unselect if clicking on the container itself, not on a section
                if (e.target === e.currentTarget) {
                  clearActiveSelection();
                }
              }}
            >
              {caseStudy.sections.sort((a, b) => a.order - b.order).map((section) => (
                <SortableCaseStudySection
                  key={section.id}
                  section={section}
                  isActive={activeSection === section.id}
                  onUpdate={(updates) => handleUpdateSection(section.id, updates)}
                  onRemove={() => handleRemoveSection(section.id)}
                  onActivate={() => {
                    activateSectionCard(section.id);
                  }}
                  canDelete={true}
                  onUpdateContentBlock={(blockId, content) => handleUpdateContentBlock(section.id, blockId, content)}
                  onRemoveContentBlock={(blockId) => handleRemoveContentBlock(section.id, blockId)}
              onAddContentBlock={(type) => {
                activateSectionCard(section.id);
                handleAddContentBlock(type);
              }}
              onAddSectionFromElement={handleAddSectionFromElement}
              sectionRef={(element) => {
                sectionRefs.current[section.id] = element;
              }}
              isCollapsed={section.isCollapsed}
              onToggleCollapse={() => handleUpdateSection(section.id, { isCollapsed: !section.isCollapsed })}
            />
          ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Section Button */}
      <div className="py-6">
        <button
          onClick={() => setShowSectionSelector(true)}
          className="w-full text-left group"
        >
          <div className="w-full rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:-translate-y-0.5">
            <div className="flex items-center justify-center px-5 py-4">
              <div className="flex justify-center items-center gap-3">
                <div className="text-center flex flex-col items-center">
                  <p className="text-sm font-semibold text-gray-900">Add Section</p>
                  <p className="text-xs text-gray-500">Insert another section to your case study</p>
                </div>
              </div>
            </div>
          </div>
        </button>
      </div>

      </div>
      {/* Add bottom padding to ensure proper scrolling */}
      <div className="h-16 lg:h-8"></div>
    </div>

      {/* Section Selector Modal */}
      <SectionSelector
        open={showSectionSelector}
        onClose={() => setShowSectionSelector(false)}
        onSelectSection={wrappedHandleAddSection}
        onSelectElement={(elementType) => {
          // Get the last section ID to add the element below it
          const lastSectionId = caseStudy.sections.length > 0
            ? caseStudy.sections[caseStudy.sections.length - 1].id
            : undefined;
          // Create a new section with this element
          handleAddSectionFromElement(elementType, lastSectionId);
        }}
        position="center"
      />

    </div>
  );
};
